import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import PageNotFound from '@/views/PageNotFound.vue';
import authGuard from './auth.guard';
import roleGuard from './role.guard';

import { Clinics } from '@/models/clinics';
import { Doctors } from '@/models/doctors';
import { Patients } from '@/models/patients';
import { Specializations } from '@/models/specializations';
import { MedicalServiceTypes } from '@/models/medicalServiceTypes';
import { Cities } from '@/models/cities';
import { Pages } from '@/models/pages';
import { Files } from '@/models/files';
import { Images } from '@/models/images';
import { Symptoms } from '@/models/symptoms';
import { CancellationReasons } from '@/models/cancellationReasons';
import { Difficulties } from '@/models/difficulties';
import { Stories } from '@/models/stories';
import { Partners } from '@/models/partners';
import { PartnerGroups } from '@/models/partnerGroups';
import { Payments } from '@/models/payments';
import { crudRoutes } from '@/lib/crud.routes';
import { Administrators } from '@/models/administrators';
import { Subscriptions } from '@/models/subscriptions';
import { SubscriptionTypes } from '@/models/subscriptionTypes';
import userModule, { RoleEnum, EVERYONE, ADMIN_ROLES } from '@/store/user';

Vue.use(VueRouter);

const routes: RouteConfig[] = [
    {
        path: '/',
        name: 'home',
        redirect: () => {
            switch (userModule.role) {
                case RoleEnum.ROLE_ADMIN:
                case RoleEnum.ROLE_SUPER_ADMIN: {
                    return { name: 'requests' };
                }
                case RoleEnum.ROLE_DOCTOR:
                default: {
                    return { name: 'patients' };
                }
            }
        }
    },
    {
        path: '/404',
        name: 'not_found',
        component: PageNotFound,
    },
    {
        path: '/requests',
        name: 'requests',
        component: () => import('../views/requests/Index.vue'),
    },
    ...crudRoutes({
        modelName: Clinics.moduleName,
        roles: ADMIN_ROLES,
    }),
    ...crudRoutes({
        modelName: Payments.moduleName,
        roles: ADMIN_ROLES,
        overwriteRouteFunctions: {
            // don't need entity routes
            create: () => [],
            edit: () => [],
        },
    }),
    ...crudRoutes({
        modelName: Doctors.moduleName,
        roles: ADMIN_ROLES,
        overwriteRouteFunctions: {
            create: (params: {path: string; meta: any; modelName: string}) => ({
                path: `${params.path}/create`,
                name: `${params.modelName}_create`,
                meta: params.meta,
                component: () => import(`../views/${params.modelName}/profile/Create.vue`),
            }),
            edit: (params: {path: string; meta: any; modelName: string}) => (
                [
                    {
                        path: `${params.path}/:${params.modelName}Id`,
                        component: () => import(`../views/ConfirmLeaving.vue`),
                        children: [
                            {
                                path: '',
                                name: `${params.modelName}_edit`,
                                meta: params.meta,
                                component: () => import(`../views/${params.modelName}/profile/Edit.vue`),
                            }
                        ]
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/specialization`,
                        component: () => import(`../views/ConfirmLeaving.vue`),
                        children: [
                            {
                                path: '',
                                name: `${params.modelName}_edit_specialization`,
                                meta: params.meta,
                                component: () => import(`../views/${params.modelName}/specialization/Edit.vue`),
                            }
                        ]
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/schedule`,
                        name: `${params.modelName}_edit_schedule`,
                        meta: params.meta,
                        component: () => import(`../views/${params.modelName}/schedule/Edit.vue`),
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/duplicate`,
                        name: `${params.modelName}_edit_duplicate`,
                        meta: params.meta,
                        component: () => import(`../views/${params.modelName}/duplicate/Edit.vue`),
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/consultations`,
                        name: `${params.modelName}_edit_consultations`,
                        meta: params.meta,
                        component: () => import(`../views/${params.modelName}/consultations/Edit.vue`),
                    }
                ]
            )
        }
    }),
    ...crudRoutes({
        modelName: Patients.moduleName,
        roles: EVERYONE,
        overwriteRouteFunctions: {
            edit: (params: {path: string; meta: any; modelName: string}) => (
                [
                    {
                        path: `${params.path}/:${params.modelName}Id`,
                        name: `${params.modelName}_edit`,
                        meta: params.meta,
                        component: () => import(`../views/${params.modelName}/Edit.vue`),
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/consultations`,
                        name: `${params.modelName}_consultations`,
                        meta: { ...params.meta, roles: [RoleEnum.ROLE_ADMIN, RoleEnum.ROLE_SUPER_ADMIN]},
                        component: () => import(`../views/${params.modelName}/consultations/List.vue`),
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/conclusions`,
                        name: `${params.modelName}_conclusions`,
                        meta: params.meta,
                        component: () => import(`../views/${params.modelName}/conclusions/List.vue`),
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/conclusions/:conclusionsId`,
                        name: `${params.modelName}_conclusions_edit`,
                        meta: params.meta,
                        component: () => import(`../views/conclusions/Edit.vue`),
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/subscriptions`,
                        name: `${params.modelName}_subscriptions`,
                        meta: params.meta,
                        component: () => import(`../views/${params.modelName}/subscriptions/List.vue`),
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/subscriptions/:patientsSubscriptionsId`,
                        name: `${params.modelName}_subscriptions_edit`,
                        meta: params.meta,
                        component: () => import(`../views/${params.modelName}/subscriptions/Edit.vue`),
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/loyalty-cards`,
                        name: `${params.modelName}_loyalty_cards`,
                        meta: params.meta,
                        component: () => import(`../views/${params.modelName}/loyaltyCards/List.vue`),
                    },
                    {
                        path: `${params.path}/:${params.modelName}Id/loyalty-cards/:cardId`,
                        name: `${params.modelName}_loyalty_cards_show`,
                        meta: params.meta,
                        component: () => import(`../views/${params.modelName}/loyaltyCards/Show.vue`),
                    },
                ]
            )
        }
    }),
    ...crudRoutes({
        modelName: Specializations.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES
    }),
    ...crudRoutes({
        modelName: MedicalServiceTypes.moduleName,
        url: MedicalServiceTypes.moduleUrl,
        routePrefix: 'directories',
        roles:ADMIN_ROLES
    }),
    ...crudRoutes({
        modelName: Cities.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES
    }),
    ...crudRoutes({
        modelName: Pages.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES
    }),
    ...crudRoutes({
        modelName: Files.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES
    }),
    ...crudRoutes({
        modelName: Images.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES
    }),
    ...crudRoutes({
        modelName: Symptoms.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES
    }),
    ...crudRoutes({
        modelName: Administrators.moduleName,
        routePrefix: 'directories',
        roles: [RoleEnum.ROLE_SUPER_ADMIN],
    }),
    ...crudRoutes({
        modelName: Subscriptions.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES,
    }),
    ...crudRoutes({
        modelName: SubscriptionTypes.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES,
    }),
    ...crudRoutes({
        modelName: CancellationReasons.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES,
    }),
    ...crudRoutes({
        modelName: Difficulties.moduleName,
        routePrefix: 'directories',
        roles: ADMIN_ROLES,
    }),
    ...crudRoutes({
        modelName: Stories.moduleName,
        roles: ADMIN_ROLES,
    }),
    ...crudRoutes({
        modelName: Partners.moduleName,
        roles: ADMIN_ROLES,
    }),
    ...crudRoutes({
        modelName: PartnerGroups.moduleName,
        routePrefix: Partners.moduleName,
        roles: ADMIN_ROLES,
    }),
    {
        path: '/calendar',
        name: 'calendar',
        component: () => import('../views/calendar/Index.vue'),
        meta: {
            requiresAuth: true,
            roles: EVERYONE,
        },
    },
    {
        path: '/consultations/:id',
        name: 'consultation',
        component: () => import('../views/consultations/Consultation.vue'),
        props: true,
        meta: {
            requiresAuth: true,
            roles: EVERYONE,
        },
    },
    {
        path: '/consultations',
        name: 'consultations',
        component: () => import('../views/consultations/List.vue'),
        meta: {
            requiresAuth: true,
            roles: EVERYONE,
        },
    },
    {
        path: '/auth',
        name: 'login',
        meta: {
            requiresAuth: false,
        },
        component: () => import('../views/login/Index.vue'),
    },
    {
        path: '*',
        redirect: {
            name: 'not_found',
        },
    }
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    scrollBehavior(__, _, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { x: 0, y: 0 };
        }
    },
});

router.beforeEach(authGuard);
router.beforeEach(roleGuard);

export const redirectToNotFound = () => {
    router.push({ name: 'not_found' });
}

export default router;
